import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Globe, Info } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { DataTable } from "@/components/app/data-table";
import { DeployTimeline } from "@/components/app/deploy-timeline";
import { DomainStatus } from "@/components/app/domain-status";
import { EmptyState } from "@/components/app/empty-state";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { apiFetch } from "@/lib/api";
import { deployQueries, domainQueries, siteQueries } from "@/lib/queries";
import type { Domain } from "@/lib/schemas";
import { formatDate } from "@/lib/utils";

export const Route = createFileRoute("/_authed/sites/$siteId/")({
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(deployQueries.forSite(params.siteId)),
      context.queryClient.ensureQueryData(domainQueries.forSite(params.siteId)),
    ]),
  component: SiteOverview,
});

const domainColumns: ColumnDef<Domain>[] = [
  {
    accessorKey: "hostname",
    header: "Hostname",
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.hostname}</span>,
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => (
      <DomainStatus
        verificationStatus={row.original.verification_status}
        certificateStatus={row.original.certificate_status}
      />
    ),
  },
  {
    accessorKey: "created_at",
    header: "Added",
    cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">
        {formatDate(row.original.created_at)}
      </span>
    ),
  },
];

function SiteOverview() {
  const { siteId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: site } = useSuspenseQuery(siteQueries.detail(siteId));
  const { data: deploys } = useSuspenseQuery(deployQueries.forSite(siteId));
  const domainsBase = useSuspenseQuery(domainQueries.forSite(siteId));

  // Background-poll while any domain is provisioning so verification + SSL
  // status flip without a manual reload.
  const hasPending = domainsBase.data.items.some(
    (d) =>
      d.verification_status === "pending" ||
      d.certificate_status === "pending" ||
      d.certificate_status === "provisioning",
  );
  useQuery({
    ...domainQueries.forSite(siteId),
    refetchInterval: hasPending ? 10_000 : false,
  });

  const [showDelete, setShowDelete] = useState(false);
  const [confirmName, setConfirmName] = useState("");

  const remove = useMutation({
    mutationFn: () => apiFetch(`/sites/${siteId}`, z.unknown(), { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteQueries.all });
      toast.success("Site deleted");
      navigate({ to: "/sites" });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  return (
    <div className="space-y-12">
      {/* ── Domains ─────────────────────────────────────────────── */}
      <section>
        <div className="mb-4 flex items-center gap-2">
          <h2 className="font-display text-lg">Domains</h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="How to manage domains"
                  className="inline-flex size-4 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Info className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                Managed in <span className="font-mono">.sandbar/config.toml</span>. Edit{" "}
                <span className="font-mono">[[domains]]</span> and push to change.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {domainsBase.data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No custom domains. Add a <span className="font-mono">[[domains]]</span> entry to{" "}
            <span className="font-mono">.sandbar/config.toml</span> and deploy.
          </p>
        ) : (
          <DataTable
            columns={domainColumns}
            data={domainsBase.data.items}
            emptyLabel="No domains"
          />
        )}
      </section>

      {/* ── Deploys ─────────────────────────────────────────────── */}
      <section>
        <h2 className="mb-4 font-display text-lg">Deploys</h2>
        {deploys.data.length === 0 ? (
          <EmptyState
            title="No deploys yet"
            description="Run `sandbar deploy` from your project."
            icon={Globe}
          />
        ) : (
          <DeployTimeline
            deploys={deploys.data}
            siteSlug={site.slug}
            productionBranch={site.production_branch ?? "main"}
            activeDeployId={site.active_deploy_id}
          />
        )}
      </section>

      {/* ── Danger zone ─────────────────────────────────────────── */}
      <section>
        <Card className="border-destructive/40 p-6">
          <h2 className="mb-1 font-display text-lg text-destructive">Danger zone</h2>
          <p className="mb-5 text-xs text-muted-foreground">Deleting a site is permanent.</p>
          <Button variant="destructive" onClick={() => setShowDelete(true)}>
            Delete site
          </Button>
        </Card>
      </section>

      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete site</DialogTitle>
            <DialogDescription>
              Type the site name <span className="font-mono">{site.name}</span> to confirm.
            </DialogDescription>
          </DialogHeader>
          <Label htmlFor="confirm-name" className="text-xs text-muted-foreground">
            Type the site name to confirm
          </Label>
          <Input
            id="confirm-name"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirmName !== site.name || remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
